// Shared pickup / delivery location summary used by the conversation
// reservation panel and the "Request a change" booking modal. Lives in @/lib
// (not the page module) so a shared component can import it without creating a
// component → page chunk cycle — the prod-only module-init TDZ white-screen
// class of bug. Pure: imports nothing.

export interface LocationSummaryInput {
  isDelivery: boolean | null | undefined;
  address: string | null | undefined;
  airportCode: string | null | undefined;
  airportName?: string | null | undefined;
  distance: number | null | undefined;
  distanceUnit: string;
  carCity?: string | null;
  carState?: string | null;
  // Optional override for the self-pickup primary line. The right-hand
  // reservation panel uses the default "Pick-up at car's location"; the
  // change-request modal passes "Free pickup at the car's location" so
  // its copy stays consistent with the renter checkout.
  selfPickupLabel?: string;
  // Translator. Accepts the lightweight (key, fallback, opts) shape we
  // use everywhere — and the react-i18next `TFunction` satisfies it because
  // `string` is assignable to its `defaultValue` overload position.
  t: LocationSummaryT;
}

// Narrow translator shape we actually use. Distinct from the
// react-i18next TFunction (which is heavily-overloaded for namespace
// inference) — but a TFunction is structurally assignable here once
// the call site narrows the result with `String(...)`.
export type LocationSummaryT = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

export function getLocationSummary(input: LocationSummaryInput): { primary: string; secondary: string | null } | null {
  const { isDelivery, address, airportCode, airportName, distance, distanceUnit, carCity, carState, selfPickupLabel, t } = input;
  const distanceLine = distance != null
    ? t("booking.delivery_distance", "Delivery distance: {{miles}} {{unit}}", { miles: distance, unit: distanceUnit })
    : null;
  if (!isDelivery) {
    const hasCar = !!(carCity || carState);
    if (!hasCar && !address && !airportCode && distance == null) return null;
    return {
      primary: selfPickupLabel ?? t("conversation.location_pickup_at_car", "Pick-up at car's location"),
      secondary: hasCar ? [carCity, carState].filter(Boolean).join(", ") : null,
    };
  }
  if (airportCode) {
    return {
      primary: airportName ? `${airportName} (${airportCode})` : airportCode,
      secondary: null,
    };
  }
  if (address) {
    return { primary: address, secondary: distanceLine };
  }
  // Task #1676: delivery trip with neither persisted address nor
  // airport. Always render the literal "Delivery to your address"
  // fallback — the modal additionally fires a one-shot reverse
  // geocode that may upgrade `address` and re-render with the real
  // street, but we never want to drop back to the self-pickup copy
  // for a delivery trip.
  return {
    primary: t("checkout.delivery_to_address", "Delivery to your address"),
    secondary: distanceLine,
  };
}
